import { useState } from "react";
import { api } from "@lib/orpc-client";
import * as Sentry from "@sentry/react";

export type PpobProductCategory = string;

export type PpobProduct = {
  id: number;
  buyerSkuCode: string;
  productName: string;
  productNameEn: string;
  category: PpobProductCategory;
  brand: string;
  type: string;
  sellerName: string;
  providerPrice: number;
  sellingPrice: number;
  sellingPriceNet: number;
  adminFee: number;
  platformFee: number;
  buyerProductStatus: boolean;
  sellerProductStatus: boolean;
  productAmount: string;
  unlimitedStock: boolean;
  stock: number | null;
  multi: boolean;
  startCutOff: string | null;
  endCutOff: string | null;
  desc: string | null;
  descEn: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PpobProductsResponse = {
  page: number;
  last_page: number;
  total_hits: number;
  result: PpobProduct[];
};

type FetchProductsParams = {
  category?: PpobProductCategory;
  brand?: string;
  type?: string;
  search?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
};

export const usePpobProducts = () => {
  const [products, setProducts] = useState<PpobProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    lastPage: number;
    totalHits: number;
  } | null>(null);

  const fetchProducts = async (params?: FetchProductsParams) => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const response = (await api.wallet.ppob.products({
        category: params?.category,
        brand: params?.brand,
        type: params?.type,
        search: params?.search,
        page: params?.page,
        size: params?.size,
        sortBy: params?.sortBy,
        sortDirection: params?.sortDirection,
      })) as PpobProductsResponse;

      setProducts(response.result);
      setPagination({
        page: response.page,
        lastPage: response.last_page,
        totalHits: response.total_hits,
      });
      setIsLoading(false);
      return response;
    } catch (err: any) {
      setIsError(true);
      setIsLoading(false);
      setError(
        err.responseBody?.error?.message ||
          err.message ||
          "Failed to fetch products"
      );
      Sentry.captureException(err, {
        tags: { feature: "ppob", service: "products" },
        extra: { params },
      });
      return null;
    }
  };

  const clearProducts = () => {
    setProducts([]);
    setPagination(null);
  };

  return {
    products,
    isLoading,
    isError,
    error,
    pagination,
    fetchProducts,
    clearProducts,
  };
};
